import React from "react";
import styled from "styled-components";
import { AdminButton } from "../action_engine";

import logger from "../../../utils/logger";

export default class AdminPanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      error: null,
      hasError: false,
      retryKey: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      error,
      hasError: true,
    };
  }

  componentDidCatch(error, errorInfo) {
    const {
      panelMode,
      panelTitle,
    } = this.props;

    logger.error("Q360 Admin panel rendering failed", {
      panelMode,
      panelTitle,
      error,
      componentStack: errorInfo?.componentStack || null,
    });
  }

  componentDidUpdate(previousProps) {
    const panelChanged =
      previousProps.panelMode !== this.props.panelMode ||
      previousProps.panelKey !== this.props.panelKey;

    if (panelChanged && this.state.hasError) {
      this.setState((currentState) => ({
        error: null,
        hasError: false,
        retryKey: currentState.retryKey + 1,
      }));
    }
  }

  handleRetry = () => {
    this.setState((currentState) => ({
      error: null,
      hasError: false,
      retryKey: currentState.retryKey + 1,
    }));
  };

  render() {
    const {
      children,
      onClose,
      panelTitle,
    } = this.props;

    const {
      hasError,
      retryKey,
    } = this.state;

    if (hasError) {
      return (
        <ErrorState role="alert" aria-live="assertive">
          <ErrorIcon aria-hidden="true">!</ErrorIcon>

          <ErrorCopy>
            <ErrorTitle>
              Ce panneau n’a pas pu être affiché
            </ErrorTitle>

            <ErrorDescription>
              Une erreur inattendue est survenue dans le panneau
              {panelTitle ? ` « ${panelTitle} »` : ""}. Le reste du
              Dashboard reste disponible.
            </ErrorDescription>
          </ErrorCopy>

          <ErrorActions>
            <RetryButton
              type="button"
              onClick={this.handleRetry}
            >
              Réessayer
            </RetryButton>

            <CloseButton
              type="button"
              onClick={onClose}
            >
              Fermer
            </CloseButton>
          </ErrorActions>
        </ErrorState>
      );
    }

    return (
      <React.Fragment key={retryKey}>
        {children}
      </React.Fragment>
    );
  }
}

const ErrorState = styled.section`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem;
  border: 1px solid rgba(255, 180, 180, 0.28);
  border-radius: 18px;
  background: rgba(120, 25, 36, 0.2);
  color: #f7f7f7;

  @media (max-width: 760px) {
    grid-template-columns: auto minmax(0, 1fr);

    & > :last-child {
      grid-column: 1 / -1;
    }
  }
`;

const ErrorIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #fff9ea;
  color: #8a1f2d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 950;
`;

const ErrorCopy = styled.div`
  min-width: 0;
`;

const ErrorTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
`;

const ErrorDescription = styled.p`
  margin: 0.35rem 0 0;
  color: rgba(247, 247, 247, 0.76);
  font-size: 0.9rem;
  line-height: 1.45;
`;

const ErrorActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem;
`;

const BaseButton = styled(AdminButton)`
  min-height: 36px;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 850;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #fff9ea;
    outline-offset: 2px;
  }
`;

const RetryButton = styled(BaseButton)`
  border: none;
  background: #fff9ea;
  color: #0b1328;
`;

const CloseButton = styled(BaseButton)`
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: transparent;
  color: #f7f7f7;
`;
